import { useState } from "react";
import { useNavigate } from "react-router-dom";
import SiteNavbar from "@/components/feature/SiteNavbar";
import SiteFooter from "@/components/feature/SiteFooter";

const pricingTable = [
  {
    category: "Tree Trimming / Pruning",
    icon: "ri-scissors-cut-line",
    rows: [
      { size: "Small (under 30 ft)", low: "$150", high: "$400", avg: "$275" },
      { size: "Medium (30–60 ft)", low: "$400", high: "$900", avg: "$650" },
      { size: "Large (60+ ft)", low: "$900", high: "$1,800", avg: "$1,350" },
    ],
  },
  {
    category: "Tree Removal",
    icon: "ri-delete-bin-line",
    rows: [
      { size: "Small (under 30 ft)", low: "$200", high: "$700", avg: "$450" },
      { size: "Medium (30–60 ft)", low: "$700", high: "$1,500", avg: "$1,100" },
      { size: "Large (60+ ft)", low: "$1,500", high: "$3,000+", avg: "$2,200" },
    ],
  },
  {
    category: "Stump Grinding",
    icon: "ri-tools-line",
    rows: [
      { size: "Small stump (under 12\")", low: "$100", high: "$200", avg: "$150" },
      { size: "Medium stump (12–24\")", low: "$150", high: "$300", avg: "$225" },
      { size: "Large stump (24\"+)", low: "$250", high: "$400", avg: "$325" },
    ],
  },
  {
    category: "Emergency Tree Removal",
    icon: "ri-alarm-warning-line",
    rows: [
      { size: "Small emergency", low: "$500", high: "$1,000", avg: "$750" },
      { size: "Medium emergency", low: "$1,000", high: "$1,800", avg: "$1,400" },
      { size: "Large / storm damage", low: "$1,500", high: "$2,500", avg: "$2,000" },
    ],
  },
];

const faqs = [
  {
    q: "How often should I trim my trees?",
    a: "Most trees benefit from trimming every 3–5 years. Fast-growing species like willows or poplars may need annual attention, while slow-growing oaks can go 5–7 years between trims. Young trees should be shaped every 2–3 years to establish good structure.",
  },
  {
    q: "What's the best time of year to trim trees?",
    a: "Late winter or early spring (before new growth) is ideal for most species. Trimming during dormancy reduces stress on the tree and minimizes disease risk. However, dead or hazardous branches should be removed any time of year.",
  },
  {
    q: "Does tree trimming increase home value?",
    a: "Yes — well-maintained trees can add 10–15% to property value according to the USDA Forest Service. Proper trimming improves curb appeal, reduces liability from falling branches, and keeps trees healthy longer.",
  },
  {
    q: "What's included in a free quote?",
    a: "A free quote typically includes an on-site assessment of the tree(s), a breakdown of labor and equipment costs, a timeline for the work, and confirmation of the contractor's license and insurance. There's no obligation to hire.",
  },
  {
    q: "Is tree removal covered by homeowners insurance?",
    a: "It depends. If a tree falls due to a covered peril (storm, lightning) and damages a structure, most policies cover removal. Routine removal of a healthy tree is generally not covered. Always check with your insurer first.",
  },
  {
    q: "How do I know if a tree needs to be removed vs. trimmed?",
    a: "Signs a tree may need removal: more than 50% of the tree is damaged or dead, the trunk has large cracks or cavities, the tree is leaning significantly, or roots are lifting pavement. A certified arborist can give you a definitive answer.",
  },
];

const costFactors = [
  { icon: "ri-ruler-line", title: "Tree Height & Diameter", desc: "The single biggest cost driver. Taller, wider trees require more labor, larger equipment, and longer cleanup time." },
  { icon: "ri-map-pin-line", title: "Location & Accessibility", desc: "Trees near power lines, structures, or fences cost more due to extra care required. Urban areas also tend to have higher labor rates." },
  { icon: "ri-heart-pulse-line", title: "Tree Health & Condition", desc: "Dead, diseased, or structurally compromised trees are more unpredictable and dangerous to work on, increasing cost." },
  { icon: "ri-calendar-2-line", title: "Time of Year", desc: "Winter and early spring are typically the slowest seasons — you may get 10–20% discounts by scheduling during off-peak months." },
  { icon: "ri-truck-line", title: "Debris Removal", desc: "Hauling away wood chips, branches, and logs adds $50–$150 to most jobs. Some contractors include it; always ask upfront." },
  { icon: "ri-building-line", title: "Local Market Rates", desc: "Labor costs vary significantly by region. California, New York, and other high-cost states typically run 20–40% above the national average." },
];

export default function CostGuidePage() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-white font-sans">
      <SiteNavbar />

      {/* Hero */}
      <section
        className="relative pt-32 pb-20 md:pt-40 md:pb-28 flex items-center"
        style={{
          backgroundImage: "url('https://public.readdy.ai/ai/img_res/4fe954c32bafc424b99f3d5458ca22c5.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#1B4332]/90 via-[#1B4332]/75 to-[#1B4332]/40"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full">
          <div className="max-w-2xl">
            <span className="inline-block bg-green-400/20 border border-green-400/40 text-green-300 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">
              Complete Pricing Guide · Updated 2026
            </span>
            <h1 className="text-4xl md:text-6xl font-extrabold text-white leading-tight mb-5">
              Tree Trimming &amp; Removal Cost Guide
            </h1>
            <p className="text-white/80 text-lg md:text-xl leading-relaxed mb-8">
              Real ballpark numbers for tree trimming, removal, and stump grinding — plus expert tips on what drives costs up or down in your area.
            </p>
            <button
              onClick={() => navigate("/free-quote")}
              className="inline-flex items-center gap-3 bg-white text-[#1B4332] font-bold text-base px-8 py-4 rounded-full hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Get My Free Local Quote
              <span className="w-8 h-8 flex items-center justify-center bg-[#1B4332] rounded-full">
                <i className="ri-arrow-right-line text-white text-sm"></i>
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Quick Summary Bar */}
      <div className="bg-[#1B4332] py-5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          {[
            { label: "Small Tree Trim", value: "$150–$400" },
            { label: "Medium Tree Trim", value: "$400–$900" },
            { label: "Large Tree Trim", value: "$900–$1,800" },
            { label: "Stump Grinding", value: "$100–$400" },
          ].map((item) => (
            <div key={item.label}>
              <p className="text-green-300 font-extrabold text-xl md:text-2xl">{item.value}</p>
              <p className="text-white/60 text-xs mt-0.5">{item.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Pricing Tables */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-[#E8F5E9] text-[#1B4332] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
              Detailed Pricing
            </span>
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#1B4332] mb-4">
              Cost Breakdown by Service
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              These ranges are based on national averages. Your local quote may vary — get a free estimate to see exact pricing in your zip code.
            </p>
          </div>

          <div className="flex flex-col gap-10">
            {pricingTable.map((section) => (
              <div key={section.category} className="rounded-2xl border border-gray-100 overflow-hidden">
                <div className="bg-[#1B4332] px-6 py-4 flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-white/15 rounded-lg">
                    <i className={`${section.icon} text-white text-lg`}></i>
                  </div>
                  <h3 className="text-white font-bold text-lg">{section.category}</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#F1F8F4] text-[#1B4332]">
                        <th className="text-left px-6 py-3 font-semibold">Tree / Stump Size</th>
                        <th className="text-center px-6 py-3 font-semibold">Low End</th>
                        <th className="text-center px-6 py-3 font-semibold">High End</th>
                        <th className="text-center px-6 py-3 font-semibold">Typical Average</th>
                      </tr>
                    </thead>
                    <tbody>
                      {section.rows.map((row, i) => (
                        <tr key={row.size} className={i % 2 === 0 ? "bg-white" : "bg-[#F8FAF9]"}>
                          <td className="px-6 py-4 text-gray-700 font-medium">{row.size}</td>
                          <td className="px-6 py-4 text-center text-gray-500">{row.low}</td>
                          <td className="px-6 py-4 text-center text-gray-500">{row.high}</td>
                          <td className="px-6 py-4 text-center font-bold text-[#1B4332]">{row.avg}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cost Factors */}
      <section className="py-20 bg-[#F1F8F4]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#1B4332] mb-3">
              What Affects Your Tree Service Cost?
            </h2>
            <p className="text-gray-500 text-base max-w-xl mx-auto">
              Understanding these factors helps you get a fair price and avoid overpaying.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {costFactors.map((f) => (
              <div key={f.title} className="bg-white rounded-2xl p-6 flex flex-col gap-3">
                <div className="w-11 h-11 flex items-center justify-center bg-[#1B4332] rounded-xl">
                  <i className={`${f.icon} text-white text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1B4332] text-base">{f.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#1B4332] mb-3">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-500 text-base">Everything homeowners ask before hiring a tree service.</p>
          </div>
          <div className="flex flex-col gap-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-gray-100 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer hover:bg-[#F8FAF9] transition-colors"
                >
                  <span className="font-semibold text-[#1B4332] text-sm md:text-base pr-4">{faq.q}</span>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className={`${openFaq === i ? "ri-subtract-line" : "ri-add-line"} text-[#1B4332] text-lg`}></i>
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 text-gray-500 text-sm leading-relaxed border-t border-gray-50">
                    <p className="pt-4">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-[#1B4332]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
            Ready to Get Your Exact Local Price?
          </h2>
          <p className="text-white/70 text-base mb-8">
            Skip the guesswork. Get a free, no-obligation quote from a licensed pro in your area — usually within 24 hours.
          </p>
          <button
            onClick={() => navigate("/free-quote")}
            className="inline-flex items-center gap-3 bg-white text-[#1B4332] font-bold text-base px-8 py-4 rounded-full hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            Get My Free Quote <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
